import React, { useEffect, useMemo, useState } from 'react';
import { useMEMTranslation } from '../utils/text-utils';
import { Button } from '../providers/harmony-scope/lazy-enablers';
import { useIsUBI } from '../utils/participation-incentive-type-hook';
import { useIsFed } from '../utils/utils-fed';
import {
  MSX_GUIDE_LINK,
  FED_UBI_GUIDE_LINK,
  FED_RBI_GUIDE_LINK,
  MSX_UBI_GUIDE_LINK,
  GENERIC_GUIDE_LINK,
  FED_MANAGER_RBI_GUIDE_LINK,
  MANAGER_GUIDE_LINK,
  MANAGER_UBI_GUIDE_LINK,
  FED_CALCULATED_ATTAINMENT_LINK,
  CALCULATED_ATTAINMENT_LINK,
  MONTHLY_VIEW_LINK,
} from '../constants/links';

interface IpropsCustomText {
  customText?: string | undefined;
}

interface IpropsManagerGuideLink {
  hasRBI: boolean;
  hasUBI: boolean;
}

const useDetermineWorkspaceGuideLink = () => {
  const isUBI = useIsUBI();
  const isFedVal = useIsFed();
  const [workspaceLink, setWorkspaceLink] = useState<string>(MSX_GUIDE_LINK);

  useEffect(() => {
    if (isFedVal) {
      if (isUBI) {
        setWorkspaceLink(FED_UBI_GUIDE_LINK);
      } else {
        setWorkspaceLink(FED_RBI_GUIDE_LINK);
      }
    } else if (isUBI) {
      setWorkspaceLink(MSX_UBI_GUIDE_LINK);
    } else {
      setWorkspaceLink(MSX_GUIDE_LINK);
    }
  }, [isFedVal, isUBI]);

  return workspaceLink;
};

export const CorpNetSuffix = () => {
  const { t } = useMEMTranslation();
  const isFedVal = useIsFed();

  return isFedVal ? (
    <>
      &nbsp;
      {t('infoICGuideCorpNetSuffix')}
    </>
  ) : null;
};

export const ICGuideLink = (props: IpropsCustomText) => {
  const { customText } = props;
  const { t } = useMEMTranslation();

  return (
    <>
      {customText}
      &nbsp;
      <Button href={GENERIC_GUIDE_LINK} appearance="link" target="_blank" external>
        {t('infoICGuideLinkText')}
      </Button>
      <CorpNetSuffix />
    </>
  );
};

export const ICWorkspaceGuideLink = () => {
  const guideLink = useDetermineWorkspaceGuideLink();
  const { t } = useMEMTranslation();

  return (
    <>
      {t('infoICGuideLeadIn')}
      &nbsp;
      <Button href={guideLink} appearance="link" target="_blank" external>
        {t('infoICGuideLinkText')}
      </Button>
      <CorpNetSuffix />
    </>
  );
};

export const ICGuideLinkCustomLeadInText = (props: IpropsCustomText) => {
  const guideLink = useDetermineWorkspaceGuideLink();
  const { t } = useMEMTranslation();
  const { customText } = props;

  return (
    <>
      {customText}
      &nbsp;
      <Button href={guideLink} appearance="link" target="_blank" external>
        {t('infoICGuideLinkText')}
      </Button>
      <CorpNetSuffix />
    </>
  );
};

export const ICGuideLinkCustomLinkText = (props: IpropsCustomText) => {
  const guideLink = useDetermineWorkspaceGuideLink();
  const { customText } = props;

  return (
    <>
      <Button href={guideLink} appearance="link" target="_blank" external>
        {customText}
      </Button>
      <CorpNetSuffix />
    </>
  );
};

export const ICManagerGuideLink = (props: IpropsManagerGuideLink) => {
  const { hasRBI, hasUBI } = props;
  const { t } = useMEMTranslation();
  const isFedVal = useIsFed();
  const guideLinkRBI = useMemo(() => (isFedVal ? FED_MANAGER_RBI_GUIDE_LINK : MANAGER_GUIDE_LINK), [isFedVal]);
  const guideLinkUBI = useMemo(() => (isFedVal ? MANAGER_UBI_GUIDE_LINK : FED_UBI_GUIDE_LINK), [isFedVal]); // No manager fed link by design for UBI

  return (
    <>
      {t('infoICManagerGuideLeadIn')}
      &nbsp;
      {hasRBI && (
        <Button href={guideLinkRBI} appearance="link" target="_blank" external>
          {t('infoICRBIManagerGuideLinkText')}
        </Button>
      )}
      {hasUBI && (
        <Button href={guideLinkUBI} appearance="link" target="_blank" external>
          {t('infoICUBIManagerGuideLinkText')}
        </Button>
      )}
      &nbsp;
      {t('infoICManagerGuideLeadOut')}
      <CorpNetSuffix />
    </>
  );
};

export const ICCalculateAttainmentGuideLink = () => {
  const isFed = useIsFed();
  const guideLink = useMemo(() => (isFed ? FED_CALCULATED_ATTAINMENT_LINK : CALCULATED_ATTAINMENT_LINK), [isFed]);
  const { t } = useMEMTranslation();

  return (
    <>
      {t('infoICGuideLeadIn')}
      &nbsp;
      <Button href={guideLink} appearance="link" target="_blank" external>
        {t('infoICGuideLinkText')}
      </Button>
      <CorpNetSuffix />
    </>
  );
};

export const ICMonthlyViewGuideLink = () => {
  const { t } = useMEMTranslation();

  return (
    <>
      {t('infoICGuideLeadIn')}
      &nbsp;
      <Button href={MONTHLY_VIEW_LINK} appearance="link" target="_blank" external>
        {t('infoICGuideLinkText')}
      </Button>
      <CorpNetSuffix />
    </>
  );
};
